import { Button } from './ui/button';
import { ArrowRight, Clock, Shield, Star, Users, Calendar, Phone } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { NextGenImage } from './NextGenImage';
import heroMechanicScene from '@/assets/hero-mobile-mechanic-scene.jpg';
// Using public assets path for consistency with HTML preload
import { useEffect } from 'react';

export const HeroSection = () => {
  const navigate = useNavigate();
  
  useEffect(() => {
    // Clean up any potential HTML artifacts
    const htmlHeroImage = document.getElementById('hero-bg-picture');
    if (htmlHeroImage) {
      htmlHeroImage.remove();
    }
  }, []);
  
  const scrollToQuote = () => {
    // Use requestAnimationFrame to prevent forced reflows
    requestAnimationFrame(() => {
      document.getElementById('quote')?.scrollIntoView({
        behavior: 'smooth'
      });
    });
  };

  return (
    <section id="home" className="min-h-screen relative flex items-center justify-center overflow-hidden">
      {/* Hero background with mobile mechanic scene */}
      <div className="absolute inset-0">
        <NextGenImage
          src={heroMechanicScene}
          alt="Professional Automotive Engine Parts and Components - Gold Coast Mobile Mechanic"
          className="w-full h-full object-cover object-center opacity-95 transition-opacity duration-500"
          priority={true}
          loading="eager"
          fetchPriority="high"
          decoding="sync"
        />
      </div>
      
      {/* Enhanced overlay for text readability */}
      <div className="absolute inset-0 bg-gradient-to-br from-black/50 via-black/30 to-black/60" />
      
      {/* Premium animated background elements */}
      <div className="absolute inset-0 opacity-30">
        <div className="absolute top-1/4 left-1/4 w-3 h-3 bg-primary rounded-full animate-pulse shadow-[var(--shadow-glow)]" />
        <div className="absolute top-3/4 right-1/4 w-2 h-2 bg-primary/60 rounded-full animate-pulse delay-1000 shadow-lg" />
        <div className="absolute top-1/2 right-1/3 w-2.5 h-2.5 bg-primary/40 rounded-full animate-pulse delay-500" />
        <div className="absolute bottom-1/4 left-1/3 w-1.5 h-1.5 bg-primary/30 rounded-full animate-pulse delay-700" />
      </div>
      
      {/* Enhanced corner gradients */}
      <div className="absolute top-0 right-0 w-[40rem] h-[40rem] bg-gradient-to-bl from-primary/20 via-primary/10 to-transparent rounded-full blur-3xl opacity-70" />
      <div className="absolute bottom-0 left-0 w-[40rem] h-[40rem] bg-gradient-to-tr from-primary/15 via-primary/8 to-transparent rounded-full blur-3xl opacity-70" />
      
      {/* Premium content container */}
      <div className="relative z-10 px-4 sm:px-6 lg:px-8 max-w-6xl mx-auto text-center pt-12 lg:pt-4 pb-12">
        {/* Hero Content */}
        <div className="space-y-6 sm:space-y-8">
          <div className="space-y-4 sm:space-y-6">
            <h1 className="text-4xl sm:text-5xl lg:text-7xl font-bold text-white leading-tight tracking-tight mobile-text-center">
              Gold Coast's #1
            </h1>
            <h2 className="gradient-text text-3xl sm:text-4xl lg:text-6xl font-bold mobile-text-center">
              Mobile Mechanic
            </h2>
            <div className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white/90 tracking-wide mobile-text-center">
              WE COME TO YOU
            </div>
          </div>

          <p className="text-lg sm:text-xl md:text-2xl text-white/90 leading-relaxed max-w-4xl mx-auto font-medium mobile-text-center mobile-px-4">
            Professional automotive services delivered to your location across the Gold Coast. 
            Quality repairs, transparent pricing, and exceptional customer service.
          </p>

          {/* Trust indicators */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-6 max-w-5xl mx-auto pt-2 mobile-grid-fix">
            <div className="trust-badge group">
              <Users className="w-6 h-6 sm:w-8 sm:h-8 text-primary mx-auto mb-3 group-hover:scale-110 transition-transform duration-300" />
              <div className="text-white font-semibold text-base sm:text-lg mb-1">1000+ Happy Customers</div>
              <div className="text-white/80 text-sm sm:text-base">Across Gold Coast</div>
            </div>
            <div className="trust-badge group">
              <Shield className="w-6 h-6 sm:w-8 sm:h-8 text-primary mx-auto mb-3 group-hover:scale-110 transition-transform duration-300" />
              <div className="text-white font-semibold text-base sm:text-lg mb-1">Transparent Pricing</div>
              <div className="text-white/80 text-sm sm:text-base">Upfront quotes, no surprises</div>
            </div>
            <div className="trust-badge group">
              <Star className="w-6 h-6 sm:w-8 sm:h-8 text-primary mx-auto mb-3 group-hover:scale-110 transition-transform duration-300 fill-primary" />
              <div className="text-white font-semibold text-base sm:text-lg mb-1">Quality Guaranteed</div>
              <div className="text-white/80 text-sm sm:text-base">Warranty included</div>
            </div>
          </div>
          
          {/* Premium CTA buttons */}
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-6 justify-center pt-4 mobile-flex-col">
            <Button onClick={() => navigate('/get-quote')} className="cta-button text-lg sm:text-xl py-6 sm:py-7 px-8 sm:px-12 group font-bold w-full sm:w-auto">
              GET FREE QUOTE NOW
              <ArrowRight className="ml-3 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Button>
            <Button onClick={() => navigate('/book-now')} className="bg-white/10 backdrop-blur-sm hover:bg-white/20 border-2 border-white text-white hover:text-white font-bold text-lg sm:text-xl py-6 sm:py-7 px-8 sm:px-12 rounded-xl transition-all duration-300 hover:scale-105 hover:shadow-xl shadow-lg w-full sm:w-auto">
              <Calendar className="mr-3 w-5 h-5" />
              BOOK SERVICE
            </Button>
          </div>

          {/* Social proof */}
          <div className="flex justify-center pt-6 text-white/90">
            <div className="flex items-center justify-center bg-card/20 backdrop-blur-sm rounded-full px-4 sm:px-6 py-3">
              <Clock className="w-4 h-4 mr-2 text-primary" />
              <span className="font-medium text-sm sm:text-base">Servicing Gold Coast Suburbs</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};